import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import type { PaginationMeta } from '@/lib/api'
import * as React from 'react'

interface AppPaginationProps {
  meta?: PaginationMeta
  onPageChange: (page: number) => void
  isLoading?: boolean
}

export function AppPagination({ meta, onPageChange, isLoading }: AppPaginationProps) {
  if (!meta || meta.totalPages <= 1) return null

  const { page, totalPages, total } = meta

  const handlePageChange = (p: number) => (e: React.MouseEvent) => {
    e.preventDefault()
    if (p < 1 || p > totalPages || isLoading) return
    onPageChange(p)
  }

  const renderPageNumbers = () => {
    const items: (number | string)[] = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(i)
      }
    } else {
      // Always add 1
      items.push(1)

      if (page > 3) {
        items.push('ellipsis-start')
      }

      if (page <= 3) {
        for (let i = 2; i <= 4; i++) {
          items.push(i)
        }
      } else if (page >= totalPages - 2) {
        for (let i = totalPages - 3; i <= totalPages - 1; i++) {
          items.push(i)
        }
      } else {
        items.push(page - 1)
        items.push(page)
        items.push(page + 1)
      }

      if (page < totalPages - 2) {
        items.push('ellipsis-end')
      }

      // Always add last
      items.push(totalPages)
    }

    const uniqueItems = Array.from(new Set(items))

    return uniqueItems.map((item, index) => {
      if (typeof item === 'string') {
        return (
          <PaginationItem key={`${item}-${index}`}>
            <PaginationEllipsis />
          </PaginationItem>
        )
      }

      return (
        <PaginationItem key={item}>
          <PaginationLink
            href="#"
            isActive={page === item}
            onClick={handlePageChange(item)}
            className="cursor-pointer"
          >
            {item}
          </PaginationLink>
        </PaginationItem>
      )
    })
  }

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-4">
      <div className="text-sm text-muted-foreground order-2 md:order-1">
        Total {total} registros
      </div>

      <Pagination className="w-auto mx-0 order-1 md:order-2">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href="#"
              onClick={handlePageChange(page - 1)}
              className={
                page <= 1 || isLoading ? 'pointer-events-none opacity-50' : 'cursor-pointer'
              }
            />
          </PaginationItem>

          {renderPageNumbers()}

          <PaginationItem>
            <PaginationNext
              href="#"
              onClick={handlePageChange(page + 1)}
              className={
                page >= totalPages || isLoading
                  ? 'pointer-events-none opacity-50'
                  : 'cursor-pointer'
              }
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  )
}
